import {
  FORMATTED_TEXT_INDENT_SPACES,
  FORMATTED_TEXT_MAX_INDENT_LEVEL,
  FORMATTED_TEXT_BULLET,
} from '../constants/bookingConst';

// "- item", "* item", "• item", "– item" — the marker must be followed by a
// space, so "-5% off" or "*important*" stay ordinary text.
const BULLET_LINE_RE = /^([-*•–])\s+(.*)$/;
// "1. item", "2) item", "a. item", "B) item". One letter only, so a sentence
// starting "No. 1 rule" or "e.g. this" is not mistaken for a list item.
const NUMBERED_LINE_RE = /^(\d{1,3}[.)]|[a-zA-Z][.)])\s+(.*)$/;

/**
 * Nesting level of a line from its leading whitespace (a tab counts as
 * FORMATTED_TEXT_INDENT_SPACES spaces), capped at the deepest level drawn.
 *
 * @param {string} leading - the line's leading spaces/tabs
 * @returns {number} 0 for an unindented line
 */
const getIndentLevel = (leading) => {
  const width = leading.replace(/\t/g, ' '.repeat(FORMATTED_TEXT_INDENT_SPACES)).length;
  return Math.min(FORMATTED_TEXT_MAX_INDENT_LEVEL, Math.floor(width / FORMATTED_TEXT_INDENT_SPACES));
};

/**
 * Splits admin-written free text into display lines that keep its layout:
 * every line break, blank lines between paragraphs, "-"/"*"/"•" bullets,
 * "1."/"a)" numbering, and indentation for nested items.
 *
 * Returns plain data rather than markup — the text is untrusted, so the
 * renderer (FormattedText) decides how each line is drawn and nothing in the
 * input can inject HTML.
 *
 * @param {string} text - free text as typed, any line-ending style
 * @returns {Array<{type: 'blank'|'bullet'|'numbered'|'text', indent: number,
 *   marker: string, content: string}>} one entry per line; empty for blank text
 */
export const parseFormattedLines = (text) => {
  const source = String(text ?? '').replace(/\r\n?/g, '\n');
  if (!source.trim()) return [];

  // Leading blank lines and trailing whitespace are dropped; the first
  // line's own indentation is kept.
  const lines = source.replace(/^(?:[ \t]*\n)+/, '').replace(/\s+$/, '').split('\n');

  return lines.map((rawLine) => {
    const [, leading, rest] = rawLine.match(/^([ \t]*)(.*)$/);
    if (!rest.trim()) return { type: 'blank', indent: 0, marker: '', content: '' };

    const indent = getIndentLevel(leading);
    const bullet = rest.match(BULLET_LINE_RE);
    if (bullet) return { type: 'bullet', indent, marker: FORMATTED_TEXT_BULLET, content: bullet[2] };

    const numbered = rest.match(NUMBERED_LINE_RE);
    if (numbered) return { type: 'numbered', indent, marker: numbered[1], content: numbered[2] };

    return { type: 'text', indent, marker: '', content: rest };
  });
};
